import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ValidationErrorsTable, {ValidationErrorsTableProps} from 'bulk-import/panes/ValidationErrorsTable';
import {
  COLUMN_TYPE,
  RowErrorCode,
  SpreadsheetValidationError,
  TaskState,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import NameTemplateInfoBanner from 'name-templates/NameTemplateInfoBanner';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {listToReadable} from 'util/NameUtil';

function getImplicitlyChangedSampleControlColumns(
  sampleControlWarnings: Array<SpreadsheetValidationError>
): Set<COLUMN_TYPE> {
  const sampleControlColumns = sampleControlWarnings.flatMap((warning) =>
    typeof warning.sampleControlColumnType === 'string'
      ? [warning.sampleControlColumnType as COLUMN_TYPE]
      : []
  );
  return new Set<COLUMN_TYPE>(sampleControlColumns);
}

function getNameTemplateUsageInfos(nameTemplateWarnings: Array<SpreadsheetValidationError>): Array<{
  itemType: string;
  overrodeCustomNames: boolean;
}> {
  const itemTypes = [
    ...new Set(
      nameTemplateWarnings.flatMap((warning) =>
        typeof warning.itemType === 'string' ? [warning.itemType] : []
      )
    ),
  ];
  return itemTypes.map((itemType) => ({
    itemType,
    overrodeCustomNames: nameTemplateWarnings
      .filter((warning) => warning.itemType === itemType)
      .some((warning) => typeof warning.overrodeCustomName === 'boolean' && warning.overrodeCustomName),
  }));
}

export default function ValidationErrors(props: ValidationErrorsTableProps): JSX.Element {
  let errorsTableProps: ValidationErrorsTableProps = props;
  let shouldDisplaySampleControlsBanner: boolean = false;
  let shouldDisplayNameTemplateUsageBanner: boolean = false;
  let inferredSampleControlColumns = new Set<COLUMN_TYPE>();
  let nameTemplateUsageInfos: Array<{itemType: string; overrodeCustomNames: boolean}> = [];
  let shouldDisplayTooManyContainersToMoveBanner: boolean = false;

  if (errorsTableProps.errorData?.state !== TaskState.FAILED && errorsTableProps.errorData?.otherWarnings) {
    const sampleControlsOtherWarnings = errorsTableProps.errorData.otherWarnings.filter(
      (warning) => warning.errorCode === RowErrorCode.SAMPLE_CONTROLS_IMPLICITLY_CHANGED
    );
    const nameTemplateOtherWarnings = errorsTableProps.errorData.otherWarnings.filter(
      (warning) => warning.errorCode === RowErrorCode.NAME_TEMPLATE_USAGE_WARNING
    );
    const remainingOtherWarnings = errorsTableProps.errorData.otherWarnings.filter(
      (warning) =>
        warning.errorCode !== RowErrorCode.SAMPLE_CONTROLS_IMPLICITLY_CHANGED &&
        warning.errorCode !== RowErrorCode.NAME_TEMPLATE_USAGE_WARNING
    );
    if (remainingOtherWarnings.length < errorsTableProps.errorData.otherWarnings.length) {
      errorsTableProps = {
        ...errorsTableProps,
        errorData: {...errorsTableProps.errorData, otherWarnings: remainingOtherWarnings},
      };
      shouldDisplaySampleControlsBanner = sampleControlsOtherWarnings.length > 0;
      shouldDisplayNameTemplateUsageBanner = nameTemplateOtherWarnings.length > 0;
      inferredSampleControlColumns = getImplicitlyChangedSampleControlColumns(sampleControlsOtherWarnings);
      nameTemplateUsageInfos = getNameTemplateUsageInfos(nameTemplateOtherWarnings);
    }
  }

  if (errorsTableProps.errorData?.state !== TaskState.FAILED && errorsTableProps.errorData?.otherErrors) {
    shouldDisplayTooManyContainersToMoveBanner = errorsTableProps.errorData.otherErrors.some(
      (e) => e.errorCode === RowErrorCode.TOO_MANY_CONTAINERS_TO_MOVE
    );
    const remainingOtherErrors = errorsTableProps.errorData.otherErrors.filter(
      (e) => e.errorCode !== RowErrorCode.TOO_MANY_CONTAINERS_TO_MOVE
    );
    errorsTableProps = {
      ...errorsTableProps,
      errorData: {...errorsTableProps.errorData, otherErrors: remainingOtherErrors},
    };
  }

  // Remove study warnings from ValidationErrorsTable since they will be displayed in StudyWarningsTable
  if (errorsTableProps.errorData?.state !== TaskState.FAILED && errorsTableProps.errorData?.studyWarnings) {
    errorsTableProps = {
      ...errorsTableProps,
      errorData: {...errorsTableProps.errorData, studyWarnings: []},
    };
  }

  const sampleControlLabels = [
    ...(inferredSampleControlColumns.has(COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS)
      ? ['restriction status']
      : []),
    ...(inferredSampleControlColumns.has(COLUMN_TYPE.SAMPLE_OWNERS) ? ['sample owners'] : []),
    ...(inferredSampleControlColumns.has(COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS)
      ? [getRestrictedSampleUsersDisplayText()]
      : []),
  ];

  return (
    <>
      {shouldDisplaySampleControlsBanner && (
        <div className={css(styles.marginBottom15px)}>
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="Sample controls for some destination containers will be updated"
            supportingInfo={
              "You didn't specify all sample control fields in your spreadsheet. By default we'll" +
              ' add any existing sample controls from a source container to its destination container' +
              ` for unspecified fields${
                sampleControlLabels.length > 0 ? ` (${listToReadable(sampleControlLabels)})` : ''
              }.`
            }
          />
        </div>
      )}
      {shouldDisplayNameTemplateUsageBanner &&
        nameTemplateUsageInfos.map(({itemType, overrodeCustomNames}) => (
          <div className={css(styles.marginBottom15px)} key={itemType}>
            <NameTemplateInfoBanner
              isPlural={true}
              itemDescription={itemType}
              supportingInfo={
                overrodeCustomNames &&
                `The values specified in the ${itemType} name column for these items will be ignored.`
              }
            />
          </div>
        ))}
      {
        // Hide the validation errors table if there is a "too many containers to move" error and no other
        // row errors, because otherwise it would lie and say there were no errors.
        (!shouldDisplayTooManyContainersToMoveBanner ||
          (errorsTableProps.errorData &&
            errorsTableProps.errorData.state !== TaskState.FAILED &&
            SpreadsheetBulkImportUtil.hasErrors(errorsTableProps.errorData))) && (
          <ValidationErrorsTable {...errorsTableProps} />
        )
      }
      {shouldDisplayTooManyContainersToMoveBanner && (
        <div className={css(styles.marginBottom15px)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Too many items to move"
            supportingInfo={
              'You can move a maximum of 50,000 containers between 2,000 locations at a time. ' +
              'Try moving fewer items.'
            }
          />
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  marginBottom15px: {
    marginBottom: '15px',
  },
});
